import React, { useRef, lazy, useState, useCallback, useEffect } from 'react';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

import ContactSelectorModal from '@/components/Modals/ContactSelectorModal';
import TemplateSelectorModal from '@/components/Modals/TemplateSelectorModal';
import CreateTemplateModal from '@/components/Modals/CreateTemplateModal';

const EmailEditor = lazy(() => import('react-email-editor'));

const CreateCampaignModal = ({
    isOpen,
    onClose,
    newCampaign,
    setNewCampaign,
    onCreateCampaign,
    organizations,
    templates,
    showCreateTemplateModal,
    setShowCreateTemplateModal,
    newTemplate,
    setNewTemplate,
    handleCreateTemplate,
}) => {
    const [showContactModal, setShowContactModal] = useState(false);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [orgSearchTerm, setOrgSearchTerm] = useState('');
    const [scenarios, setScenarios] = useState([]);

    // 🔹 NEW: selected templates per step
    const [selectedByStep, setSelectedByStep] = useState({}); // { "1": templateId, "2": templateId, ... }

    useEffect(() => {
        api.get('/api/campaigns/scenarios/')
            .then(res => setScenarios(res.data))
            .catch(err => console.error('Failed to load scenarios:', err));
    }, []);

    const handleScenarioChange = useCallback((value) => {
        setNewCampaign(prev => ({ ...prev, scenario: value }));
        setSelectedByStep({}); // reset selections when scenario changes
    }, [setNewCampaign]);

    const handleStaggerChange = useCallback((value) => {
        setNewCampaign(prev => ({ ...prev, stagger: value }));
    }, [setNewCampaign]);

    const handleCreateCampaign = async () => {
        try {
            await api.post('/api/campaigns/', {
                name: newCampaign.name,
                scenario: newCampaign.scenario,
                contacts: newCampaign.selectedContacts,
                frequency_days: Number(newCampaign.frequency),
                stagger_sends: newCampaign.stagger === 'yes',
                status: newCampaign.status,
                // 🔹 NEW: pass user choices (adjust field name to your backend)
                template_map: selectedByStep,
            });

            onCreateCampaign?.();
            onClose();
        } catch (err) {
            console.error('❌ Create campaign failed:', err);
            alert('Failed to create campaign. Check the fields.');
        }
    };

    const selectedCount = Object.keys(selectedByStep).length;

    return (
        <>
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="max-w-4xl">
                    <DialogHeader>
                        <DialogTitle>Create New Campaign</DialogTitle>
                        <DialogDescription>Set up a new outreach campaign.</DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div>
                            <Label className="mb-3" htmlFor="campaign-name">Campaign Name</Label>
                            <Input
                                id="campaign-name"
                                value={newCampaign.name || ''}
                                onChange={(e) => setNewCampaign(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="e.g., Q1 2025 School Outreach"
                            />
                        </div>

                        <div>
                            <Label className="mb-3" htmlFor="campaign-scenario">Scenario</Label>
                            <Select value={newCampaign.scenario || ''} onValueChange={handleScenarioChange}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a scenario" />
                                </SelectTrigger>
                                <SelectContent style={{ zIndex: 1000 }}>
                                    {scenarios.map((s, i) => (
                                        <SelectItem key={i} value={s.value}>{s.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label className="mb-3">Status</Label>
                            <Select
                                value={newCampaign.status || 'draft'}
                                onValueChange={(value) => setNewCampaign(prev => ({ ...prev, status: value }))}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="draft">Draft</SelectItem>
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="paused">Paused</SelectItem>
                                    <SelectItem value="waiting">Waiting for approval</SelectItem>
                                    <SelectItem value="completed">Completed</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label className="mb-3">Contacts</Label>
                            <Button variant="outline" className="w-full" onClick={() => setShowContactModal(true)}>
                                {newCampaign.selectedContacts?.length > 0
                                    ? `${newCampaign.selectedContacts.length} Contacts Selected`
                                    : 'Select Contacts'}
                            </Button>
                        </div>

                        <div>
                            <Label className="mb-3">Select Templates Per Step</Label>
                            <Button
                                variant="outline"
                                className="w-full justify-between"
                                onClick={() => setShowTemplateModal(true)}
                            >
                                <span>
                                    {selectedCount > 0
                                        ? `${selectedCount} step${selectedCount > 1 ? 's' : ''} selected`
                                        : 'Choose templates'}
                                </span>
                                {/* small summary */}
                                {selectedCount > 0 && (
                                    <span className="text-xs text-muted-foreground">
                                        {Object.keys(selectedByStep)
                                            .sort((a, b) => Number(a) - Number(b))
                                            .map(step => `Step ${step}: ${selectedByStep[step]}`)
                                            .join('  •  ')}
                                    </span>
                                )}
                            </Button>
                        </div>

                        <div className="grid grid-cols-3 gap-4">

                            <div>
                                <Label className="mb-3" htmlFor="frequency">Frequency (days)</Label>
                                <Input
                                    id="frequency"
                                    type="number"
                                    min={0}
                                    value={newCampaign.frequency ?? 0}
                                    onChange={(e) =>
                                        setNewCampaign(prev => ({ ...prev, frequency: Number(e.target.value ?? 0) }))
                                    }
                                />
                            </div>
                            <div>
                                <Label className="mb-3" htmlFor="stagger">Stagger Sends</Label>
                                <Select value={newCampaign.stagger || ''} onValueChange={handleStaggerChange}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select option" />
                                    </SelectTrigger>
                                    <SelectContent style={{ zIndex: 1000 }}>
                                        <SelectItem value="yes">Yes</SelectItem>
                                        <SelectItem value="no">No</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-2 pt-4">
                        <Button variant="outline" onClick={onClose}>Cancel</Button>
                        <Button
                            onClick={handleCreateCampaign}
                            disabled={
                                !newCampaign.name ||
                                !newCampaign.scenario ||
                                !newCampaign.selectedContacts?.length ||
                                Object.keys(selectedByStep).length === 0
                            }
                        >
                            Create Campaign
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <ContactSelectorModal
                isOpen={showContactModal}
                onClose={() => setShowContactModal(false)}
                organizations={organizations}
                newCampaign={newCampaign}
                setNewCampaign={setNewCampaign}
                orgSearchTerm={orgSearchTerm}
                setOrgSearchTerm={setOrgSearchTerm}
            />

            <TemplateSelectorModal
                isOpen={showTemplateModal}
                onClose={() => setShowTemplateModal(false)}
                templates={templates}
                newCampaign={newCampaign}
                // 🔹 pass selection state
                selectedByStep={selectedByStep}
                setSelectedByStep={setSelectedByStep}
                scenarios={scenarios}
            />

            <CreateTemplateModal
                isOpen={showCreateTemplateModal}
                onClose={() => setShowCreateTemplateModal(false)}
                newTemplate={newTemplate}
                setNewTemplate={setNewTemplate}
                scenarios={scenarios}            // expects [{value,label}]
                templates={templates}
                onCreateTemplate={handleCreateTemplate}
            />
        </>
    );
};

export default CreateCampaignModal;